export interface TrainingPageFormat {
  windowTitle: string;
  route: string;
  nextRoute?: string;
  previousRoute?: string;
  title: string;
  footnotes?: string[];
}

export interface TrainingNavigationDictionary {
  [module: string]: {
    [page: string]: TrainingPageFormat;
  };
}

export interface BaseRouteDictionary {
  [page: string]: string;
}

export const MODULE_ARRAY = [
  {
    // Axumin training
    trainingId: 1,
    modules: ['axuminAcquisition', 'axuminInterpretation'],
  },
  {
    // POSLUMA training
    trainingId: 2,
    modules: ['poslumaAcquisition', 'poslumaInterpretation'],
  },
];

export const BASE_ROUTE: BaseRouteDictionary = {
  axuminAcquisition: '/axumin/acquisition',
  axuminInterpretation: '/axumin/interpretation',
  poslumaAcquisition: '/posluma/acquisition',
  poslumaInterpretation: '/posluma/interpretation',
};

export const TRAINING_NAVIGATION: TrainingNavigationDictionary = {
  axuminInterpretation: {
    '1': {
      windowTitle: 'Welcome to Axumin Image Interpretation Training',
      previousRoute: '',
      route: '/welcome',
      nextRoute: '/isi',
      title: 'Welcome to Axumin Image Interpretation Training',
    },
    '2': {
      windowTitle: 'Axumin Indication and Important Safety Information',
      previousRoute: '/welcome',
      route: '/isi',
      nextRoute: '/moa',
      title: 'Axumin Indication and Important Safety Information',
      footnotes: [
        'PC, prostate cancer.',
        '1. Axumin<sup>®</sup> (fluciclovine F 18) Injection; US Prescribing Information; Blue Earth Diagnostics, Ltd. May 2021.',
        '2. Image adapted from: Crawford ED, et al. Urology. 2014;83(8):684-689.',
      ],
    },
    '3': {
      windowTitle: 'Axumin MOA, Molecular structure, and Biodistribution',
      previousRoute: '/isi',
      route: '/moa',
      nextRoute: '/instrumentation',
      title: 'How Does Axumin Work?',
      footnotes: [
        'ASCT2, alanine, serine, cysteine transporter 2; LAT1, L-type amino acid transporter 1; SUV<sub>mean</sub>, mean standardized uptake value.',
        '1. Axumin<sup>®</sup> (fluciclovine F 18) injection was specifically developed to enhance PET/CT imaging. Axumin. Accessed May 17, 2021. <a href="https://www.axumin.com/about/moa" target="_blank" rel="noreferrer">https://www.axumin.com/about/moa</a>',
        '2. Fuchs BC, Bode BP. <em>Semin Cancer Biol.</em> 2005;15(4):254-266.',
        '3. Oka S, et al. <em>Nucl Med Biol.</em> 2012;39(1):109-119.',
        '4. Axumin<sup>®</sup> (fluciclovine F 18) Injection; US Prescribing Information; Blue Earth Diagnostics, Ltd. May 2021.',
        '5. Schuster DM, et al. <em>J Nucl Med.</em> 2014;55(12):1986-1992.',
        '6. Blue Earth Diagnostics Ltd. Data on file. Owenius, et al. Dec 2010 Report.',
      ],
    },
    '4': {
      windowTitle: 'Axumin Administration',
      previousRoute: '/moa',
      route: '/instrumentation',
      nextRoute: '/administration',
      title: 'Instrumentation for Use with Axumin',
      footnotes: [
        '1. Axumin<sup>®</sup> (fluciclovine F 18) Injection; US Prescribing Information; Blue Earth Diagnostics, Ltd. May 2021.',
        '2. Elschot M, et al. <em>J Nucl Med.</em> 2018;59(12):1913-1917.',
        '3. Blue Earth Diagnostics Inc. Axumin<sup>®</sup> (fluciclovine F 18) Image Interpretation Training. May 2020 v2.1.',
      ],
    },
    '5': {
      windowTitle: 'Axumin Administration',
      previousRoute: '/instrumentation',
      route: '/administration',
      nextRoute: '/imageacquisition',
      title: 'Preparing the Patient for Administration',
      footnotes: [
        '1. Axumin<sup>®</sup> (fluciclovine F 18) Injection; US Prescribing Information; Blue Earth Diagnostics, Ltd. May 2021.',
        '2. Blue Earth Diagnostics Inc. Axumin<sup>®</sup> (fluciclovine F 18) Image Interpretation Training. May 2020 v2.1.',
        '3. Images acquired through Blue Earth Diagnostics sponsored studies: BED-001, LOCATE, FALCON.',
      ],
    },
    '6': {
      windowTitle: 'Image Acquisition',
      previousRoute: '/administration',
      route: '/imageacquisition',
      nextRoute: '/imagecapture',
      title: 'Image Acquisition',
      footnotes: [
        '1. Axumin<sup>®</sup> (fluciclovine F 18) Injection; US Prescribing Information; Blue Earth Diagnostics, Ltd. May 2021.',
        '2. Blue Earth Diagnostics Inc. Axumin<sup>®</sup> (fluciclovine F 18) Image Interpretation Training. May 2020 v2.1.',
        '3. Blue Earth Diagnostics Ltd. Data on file. Owenius, et al. Dec 2010 Report.',
        '4. Images acquired through Blue Earth Diagnostics sponsored studies: BED-001, LOCATE, FALCON.',
      ],
    },
    '7': {
      windowTitle: 'Image Acquisition – How to Capture the Best Image',
      previousRoute: '/imageacquisition',
      route: '/imagecapture',
      nextRoute: '/accurateimaging',
      title: 'Image Acquisition – How to Capture the Best Image',
      footnotes: [
        '1. Blue Earth Diagnostics Inc. Sample Axumin<sup>®</sup> (fluciclovine F 18) PET/CT Prostate Patient Questionnaire; 2021. Accessed May 24, 2021.',
        '2. Axumin<sup>®</sup> (fluciclovine F 18) Injection; US Prescribing Information; Blue Earth Diagnostics, Ltd. May 2021.',
        '3. Blue Earth Diagnostics Inc. Axumin<sup>®</sup> (fluciclovine F 18) Image Interpretation Training. May 2020 v2.1.',
        '4. Images acquired through Blue Earth Diagnostics sponsored studies: BED-001, LOCATE, FALCON.',
      ],
    },
    '8': {
      windowTitle: 'Recommended Protocol to Ensure the Most Accurate Imaging Interpretation',
      previousRoute: '/imagecapture',
      route: '/accurateimaging',
      nextRoute: '/qualityimage',
      title: 'Recommended Protocol to Ensure the Most Accurate Imaging Interpretation',
    },
    '9': {
      windowTitle: 'Review General Interpretation Criteria and Methodology',
      previousRoute: '/accurateimaging',
      route: '/qualityimage',
      nextRoute: '/qualityimage/casestudy',
      title: 'Recommended Protocol to Ensure Optimal Quality Image Acquisition',
      footnotes: [
        '1. Axumin<sup>®</sup> (fluciclovine F 18) Injection; US Prescribing Information; Blue Earth Diagnostics, Ltd. May 2021.',
        '2. Blue Earth Diagnostics Inc. 2020. Axumin<sup>®</sup> (fluciclovine F 18) Image Interpretation Training. May 2020 v2.1.',
        '3. McConathy J. Fluciclovine F 18 Reader Training. SNMMI/CTN Webinar. May 14, 2019. Accessed May 19, 2021. <a href="https://www.snmmilearningcenter.org/lms/activity?@curriculum.id=-1&@activity.id=6801559&@activity.bundleActivityId=-1" target="_blank" rel="noreferrer">https://www.snmmilearningcenter.org/lms/activity?@curriculum.id=-1&@activity.id=6801559&@activity.bundleActivityId=-1</a>',
        '4. Images acquired through Blue Earth Diagnostics sponsored studies: BED-001, LOCATE, FALCON.',
      ],
    },
    '10': {
      windowTitle: 'Review General Interpretation Criteria and Methodology Continued',
      previousRoute: '/qualityimage',
      route: '/qualityimage/casestudy',
      nextRoute: '/petdisplayguides',
      title: 'Recommended Protocol to Ensure Optimal Quality Image Acquisition',
      footnotes: [
        '1. Blue Earth Diagnostics Inc. Axumin<sup>®</sup> (fluciclovine F 18) Image Interpretation Training. May 2020 v2.1.',
        '2. Images acquired through Blue Earth Diagnostics sponsored studies: BED-001, LOCATE, FALCON.',
      ],
    },
    '11': {
      windowTitle: 'Review PET Window Display Guidelines',
      previousRoute: '/qualityimage/casestudy',
      route: '/petdisplayguides',
      nextRoute: '/axuminuptake',
      title: 'Recommended Protocol to Ensure Optimal Quality Image Acquisition',
      footnotes: [
        '1. Blue Earth Diagnostics Inc. Axumin<sup>®</sup> (fluciclovine F 18) Image Interpretation Training. May 2020 v2.1.',
        '2. Images acquired through Blue Earth Diagnostics sponsored studies: BED-001, LOCATE, FALCON.',
      ],
    },
    '12': {
      windowTitle: 'Assess and Evaluate Axumin Uptake in Various Settings',
      previousRoute: '/petdisplayguides',
      route: '/axuminuptake',
      nextRoute: '/axuminuptake/casestudy',
      title: 'Recommended Protocol to Ensure Optimal Quality Image Acquisition',
      footnotes: [
        '1. Blue Earth Diagnostics Inc. Axumin<sup>®</sup> (fluciclovine F 18) Image Interpretation Training. May 2020 v2.1.',
        '2. Images acquired through Blue Earth Diagnostics sponsored studies: BED-001, LOCATE, FALCON.',
      ],
    },
    '13': {
      windowTitle: 'Assess and Evaluate Axumin Uptake in Various Settings Continued',
      previousRoute: '/axuminuptake',
      route: '/axuminuptake/casestudy',
      nextRoute: '/axuminuptake/seminalvesicles',
      title: 'Recommended Protocol to Ensure Optimal Quality Image Acquisition',
      footnotes: [
        '1. Blue Earth Diagnostics Inc. Axumin<sup>®</sup> (fluciclovine F 18) Image Interpretation Training. May 2020 v2.1.',
        '2. Images acquired through Blue Earth Diagnostics sponsored studies: BED-001, LOCATE, FALCON.',
      ],
    },
    '14': {
      windowTitle: 'Assess and Evaluate Axumin Uptake in Various Settings Continued',
      previousRoute: '/axuminuptake/casestudy',
      route: '/axuminuptake/seminalvesicles',
      nextRoute: '/axuminuptake/lymphnodes',
      title: 'Recommended Protocol to Ensure Optimal Quality Image Acquisition',
      footnotes: [
        '1. Blue Earth Diagnostics Inc. Axumin<sup>®</sup> (fluciclovine F 18) Image Interpretation Training. May 2020 v2.1.',
        '2. Images acquired through Blue Earth Diagnostics sponsored studies: BED-001, LOCATE, FALCON.',
      ],
    },
    '15': {
      windowTitle: 'Assess and Evaluate Axumin Uptake in Various Settings Continued',
      previousRoute: '/axuminuptake/seminalvesicles',
      route: '/axuminuptake/lymphnodes',
      nextRoute: '/axuminuptake/bone',
      title: 'Recommended Protocol to Ensure Optimal Quality Image Acquisition',
      footnotes: [
        '1. Blue Earth Diagnostics Inc. Axumin<sup>®</sup> (fluciclovine F 18) Image Interpretation Training. May 2020 v2.1.',
        '2. Images acquired through Blue Earth Diagnostics sponsored studies: BED-001, LOCATE, FALCON.',
      ],
    },
    '16': {
      windowTitle: 'Assess and Evaluate Axumin Uptake in Various Settings Continued',
      previousRoute: '/axuminuptake/lymphnodes',
      route: '/axuminuptake/bone',
      nextRoute: '/axuminuptake/liver',
      title: 'Recommended Protocol to Ensure Optimal Quality Image Acquisition',
      footnotes: [
        '1. Blue Earth Diagnostics Inc. Axumin<sup>®</sup> (fluciclovine F 18) Image Interpretation Training. May 2020 v2.1.',
        '2. Images acquired through Blue Earth Diagnostics sponsored studies: BED-001, LOCATE, FALCON.',
      ],
    },
    '17': {
      windowTitle: 'Assess and Evaluate Axumin Uptake in Various Settings Continued',
      previousRoute: '/axuminuptake/bone',
      route: '/axuminuptake/liver',
      nextRoute: '/exclusion',
      title: 'Recommended Protocol to Ensure Optimal Quality Image Acquisition',
      footnotes: [
        '1. Blue Earth Diagnostics Inc. Axumin<sup>®</sup> (fluciclovine F 18) Image Interpretation Training. May 2020 v2.1.',
        '2. Images acquired through Blue Earth Diagnostics sponsored studies: BED-001, LOCATE, FALCON.',
        '3. Oka S, et al. J Nucl Med. 2007;48(1):46-55.',
        '4. Oka S, et al. Mol Imaging Biol. 2014;16(3):322-329.',
        '5. Kanagawa M, et al. Nucl Med Biol. 2014;41(7):545-551.',
      ],
    },
    '18': {
      windowTitle: 'Exclude Physiological Variants and Incidental Findings',
      previousRoute: '/axuminuptake/liver',
      route: '/exclusion',
      nextRoute: '/interpreport',
      title: 'Recommended Protocol to Ensure Optimal Quality Image Acquisition',
      footnotes: [
        '1. Blue Earth Diagnostics Inc. Axumin<sup>®</sup> (fluciclovine F 18) Image Interpretation Training. May 2020 v2.1.',
        '2. Schuster DM, et al. <em>J Nucl Med.</em> 2014;55(12):1986-1992.',
        '3. Images acquired through Blue Earth Diagnostics sponsored studies: BED-001, LOCATE, FALCON.',
      ],
    },
    '19': {
      windowTitle: 'Create Interpretation Report',
      previousRoute: '/exclusion',
      route: '/interpreport',
      nextRoute: '',
      title: 'Recommended Protocol to Ensure Optimal Quality Image Acquisition',
      footnotes: [
        '1. Axumin<sup>®</sup> (fluciclovine F 18) Injection; US Prescribing Information; Blue Earth Diagnostics, Ltd. May 2021.',
        '2. Axumin<sup>®</sup> (fluciclovine F 18) Imaging & Interpretation Manual (Prostate Cancer) v2.1. April 2020.',
        '3. Lowentritt BH, Kipper MS. Understanding and improving 18F-fluciclovine PET/CT reports: a guide for physicians treating patients with biochemical recurrence of prostate cancer. Prostate Cancer. 2020;2020:1929565. doi:10.1155/2020/1929565',
      ],
    },
  },
  axuminAcquisition: {
    '1': {
      windowTitle: 'Welcome to Axumin Image Acquisition Training',
      previousRoute: '',
      route: '/welcome',
      nextRoute: '/isi',
      title: 'Welcome to Axumin Image Acquisition Training',
    },
    '2': {
      windowTitle: 'Axumin Indication and Important Safety Information',
      previousRoute: '/welcome',
      route: '/isi',
      nextRoute: '/moa',
      title: 'Axumin Indication and Important Safety Information',
      footnotes: [
        'PC, prostate cancer.',
        '1. Axumin<sup>®</sup> (fluciclovine F 18) Injection; US Prescribing Information; Blue Earth Diagnostics, Ltd. May 2021.',
        '2. Image adapted from: Crawford ED, et al. Urology. 2014;83(8):684-689.',
      ],
    },
    '3': {
      windowTitle: 'Axumin MOA, Molecular structure, and Biodistribution',
      previousRoute: '/isi',
      route: '/moa',
      nextRoute: '/accurateimaging',
      title: 'How Does Axumin Work?',
      footnotes: [
        'ASCT2, alanine, serine, cysteine transporter 2; LAT1, L-type amino acid transporter 1; SUV<sub>mean</sub>, mean standardized uptake value.',
        '1. Axumin<sup>®</sup> (fluciclovine F 18) injection was specifically developed to enhance PET/CT imaging. Axumin. Accessed May 17, 2021. <a href="https://www.axumin.com/about/moa" target="_blank" rel="noreferrer">https://www.axumin.com/about/moa</a>',
        '2. Fuchs BC, Bode BP. <em>Semin Cancer Biol.</em> 2005;15(4):254-266.',
        '3. Oka S, et al. <em>Nucl Med Biol.</em> 2012;39(1):109-119.',
        '4. Axumin<sup>®</sup> (fluciclovine F 18) Injection; US Prescribing Information; Blue Earth Diagnostics, Ltd. May 2021.',
        '5. Schuster DM, et al. <em>J Nucl Med.</em> 2014;55(12):1986-1992.',
        '6. Blue Earth Diagnostics Ltd. Data on file. Owenius, et al. Dec 2010 Report.',
      ],
    },
    '4': {
      windowTitle: 'Recommended Protocol to Ensure the Most Accurate Imaging Acquisition',
      previousRoute: '/moa',
      route: '/accurateimaging',
      nextRoute: '/instrumentation',
      title: 'Recommended Protocol to Ensure Optimal Quality Image Acquisition',
    },
    '5': {
      windowTitle: 'Instrumentation for Use with Axumin',
      previousRoute: '/accurateimaging',
      route: '/instrumentation',
      nextRoute: '/patientprep',
      title: 'Recommended Protocol to Ensure Optimal Quality Image Acquisition',
      footnotes: [
        '1. Axumin<sup>®</sup> (fluciclovine F 18) Injection; US Prescribing Information; Blue Earth Diagnostics, Ltd. May 2021',
        '2. Elschot M, et al. <em>J Nucl Med.</em> 2018;59(12):1913-1917.',
        '3. Blue Earth Diagnostics Inc. Axumin<sup>®</sup> (fluciclovine F 18) Image Acquisition Training. May 2020 v2.1.',
      ],
    },
    '6': {
      windowTitle: 'Prepare the Patient',
      previousRoute: '/instrumentation',
      route: '/patientprep',
      nextRoute: '/preparation',
      title: 'Recommended Protocol to Ensure Optimal Quality Image Acquisition',
      footnotes: [
        '1. Axumin<sup>®</sup> (fluciclovine F 18) Injection; US Prescribing Information; Blue Earth Diagnostics, Ltd. May 2021.',
        '2. Blue Earth Diagnostics Inc. Axumin<sup>®</sup> (fluciclovine F 18) Image Acquisition Training. May 2020 v2.1.',
        '3. Blue Earth Diagnostics Inc. Sample Axumin<sup>®</sup> (fluciclovine F 18) PET/CT Prostate Patient Questionnaire; 2021. Accessed May 24, 2021.',
      ],
    },
    '7': {
      windowTitle: 'Prepare the Axumin Dose and Administer',
      previousRoute: '/patientprep',
      route: '/preparation',
      nextRoute: '/imageacquisition',
      title: 'Recommended Protocol to Ensure Optimal Quality Image Acquisition',
      footnotes: [
        '1. Axumin<sup>®</sup> (fluciclovine F 18) Injection; US Prescribing Information; Blue Earth Diagnostics, Ltd. May 2021.',
        '2. Blue Earth Diagnostics Inc. Axumin<sup>®</sup> (fluciclovine F 18) Image Acquisition Training. May 2020 v2.1.',
        '3. Images acquired through Blue Earth Diagnostics sponsored studies: BED-001, LOCATE, FALCON',
      ],
    },
    '8': {
      windowTitle: 'Image Acquisition',
      previousRoute: '/preparation',
      route: '/imageacquisition',
      nextRoute: '/imagescapture',
      title: 'Recommended Protocol to Ensure Optimal Quality Image Acquisition',
      footnotes: [
        '1. Axumin<sup>®</sup> (fluciclovine F 18) Injection; US Prescribing Information; Blue Earth Diagnostics, Ltd. May 2021.',
        '2. Blue Earth Diagnostics Inc. Axumin<sup>®</sup> (fluciclovine F 18) Image Acquisition Training. May 2020 v2.1.',
        '3. Blue Earth Diagnostics Ltd. Data on file. Owenius, et al. Dec 2010 Report.',
        '4. Images acquired through Blue Earth Diagnostics sponsored studies: BED-001, LOCATE, FALCON.',
      ],
    },
    '9': {
      windowTitle: 'Image Acquisition Is Important for Biodistribution',
      previousRoute: '/imageacquisition',
      route: '/imagescapture',
      nextRoute: '/imagereconstruction',
      title: 'Recommended Protocol to Ensure Optimal Quality Image Acquisition',
      footnotes: [
        '1. Axumin<sup>®</sup> (fluciclovine F 18) Injection; US Prescribing Information; Blue Earth Diagnostics, Ltd. May 2021.',
        '2. Blue Earth Diagnostics Inc. Axumin<sup>®</sup> (fluciclovine F 18) Image Acquisition Training. May 2020 v2.1.',
        '3. Blue Earth Diagnostics Inc. Sample Axumin<sup>®</sup> (fluciclovine F 18) PET/CT Prostate Patient Questionnaire; 2021. Accessed May 24, 2021.',
        '4. Images acquired through Blue Earth Diagnostics sponsored studies: BED-001, LOCATE, FALCON.',
      ],
    },
    '10': {
      windowTitle: 'Image Reconstruction',
      previousRoute: '/imagescapture',
      route: '/imagereconstruction',
      nextRoute: '/casestudies',
      title: 'Recommended Protocol to Ensure Optimal Quality Image Acquisition',
      footnotes: [
        '1. Axumin<sup>®</sup> (fluciclovine F 18) Injection; US Prescribing Information; Blue Earth Diagnostics, Ltd. May 2021.',
        '2. Blue Earth Diagnostics Inc. Axumin<sup>®</sup> (fluciclovine F 18) Image Acquisition Training. May 2020 v2.1.',
      ],
    },
    '11': {
      windowTitle: 'Image Acquisition Case Studies',
      previousRoute: '/imagereconstruction',
      route: '/casestudies',
      nextRoute: '',
      title: 'Image Acquisition Case Studies',
      footnotes: ['1. Images acquired through Blue Earth Diagnostics sponsored studies: BED-001, LOCATE, FALCON.'],
    },
  },
  poslumaAcquisition: {
    '1': {
      windowTitle: 'Welcome to the POSLUMA Image Acquisition Training',
      previousRoute: '',
      route: '/welcome',
      nextRoute: '/isi',
      title: 'Welcome to the POSLUMA Image Acquisition Training',
    },
    '2': {
      windowTitle: 'POSLUMA Indication and Important Safety Information',
      route: '/isi',
      nextRoute: '/moa',
      previousRoute: '/welcome',
      title: 'POSLUMA Indication and Important Safety Information',
      footnotes: [
        '1. POSLUMA<sup>®</sup> (flotufolastat F 18) Injection. US Prescribing information; Blue Earth Diagnostics, Ltd. 2023.',
      ],
    },
    '3': {
      windowTitle: 'Background on Prostate Cancer and POSLUMA',
      previousRoute: '/isi',
      route: '/moa',
      nextRoute: '/accurateimaging',
      title: 'Background on Prostate Cancer and POSLUMA',
      footnotes: [
        '1. Blue Earth Diagnostics Inc. POSLUMA® (flotufolastat F 18) Company Core Technical Training Manual. Version 3.0, May 2023.',
        '2. Jemal A, Bray F, Center MM, Ferlay J, Ward E, Forman D. Global cancer statistics. <em>CA Cancer J Clin.</em> 2011;61(2):69-90. doi:10.3322/caac.20107',
        '3. Froemming AT, Verma S, Eberhardt SC, et al; Expert Panel on Urologic Imaging. ACR Appropriateness Criteria® Post-treatment Follow-up Prostate Cancer. <em>J Am Coll Radiol.</em> 2018;15(5S):S132-S149. doi:10.1016/j.jacr.2018.03.019',
        '4. Elgamal AA, Holmes EH, Su SL, et al. Prostate-specific membrane antigen (PSMA): current benefits and future value. <em>Semin Surg Oncol.</em> 2000;18(1):10-16.',
        '5. Wurzer A, Di Carlo D, Schmidt A, et al. Radiohybrid ligands: a novel tracer concept exemplified by <sup>18</sup>F-or <sup>68</sup>Ga-labeled rhPSMA inhibitors. <em>J Nucl Med.</em> 2020;61(5):735-742. doi:10.2967/jnumed.119.234922',
        '6. POSLUMA® (flotufolastat F 18) Injection. US Prescribing information; Blue Earth Diagnostics, Ltd. 2023.',
      ],
    },
    '4': {
      windowTitle: 'Recommended Protocol to Ensure Optimal Quality Image Acquisition',
      previousRoute: '/moa',
      route: '/accurateimaging',
      nextRoute: '/instrumentation',
      title: 'Recommended Protocol to Ensure Optimal Quality Image Acquisition',
    },
    '5': {
      windowTitle: 'STEP 1: Instrumentation for Use With POSLUMA',
      previousRoute: '/accurateimaging',
      route: '/instrumentation',
      nextRoute: '/patientprep',
      title: 'Recommended Protocol to Ensure Optimal Quality Image Acquisition',
      footnotes: [
        '1. POSLUMA® (flotufolastat F 18) Injection. US Prescribing information; Blue Earth Diagnostics, Ltd. 2023.',
        '2. Blue Earth Diagnostics Inc. POSLUMA® (flotufolastat F 18) Company Core Technical Training Manual. Version 3.0, May 2023.',
      ],
    },
    '6': {
      windowTitle: 'STEP 2: Patient Preparation and Counseling',
      previousRoute: '/instrumentation',
      route: '/patientprep',
      nextRoute: '/preparation',
      title: 'Recommended Protocol to Ensure Optimal Quality Image Acquisition',
      footnotes: [
        'POSLUMA® (flotufolastat F 18) Injection. US Prescribing information; Blue Earth Diagnostics, Ltd. 2023.',
      ],
    },
    '7': {
      windowTitle: 'STEP 3: Preparation and Administration of POSLUMA Dose',
      previousRoute: '/patientprep',
      route: '/preparation',
      nextRoute: '/imagecapture',
      title: 'Recommended Protocol to Ensure Optimal Quality Image Acquisition',
      footnotes: [
        '1. POSLUMA® (flotufolastat F 18) Injection. US Prescribing information; Blue Earth Diagnostics, Ltd. 2023.',
        '2. Tolvanen T, Kalliokoski K, Malaspina S, et al. Safety, biodistribution, and radiation dosimetry of <sup>18</sup>F-rhPSMA-7.3 in healthy adult volunteers. <em>J Nucl Med.</em> 2021;62(5):679-684. doi:10.2967/jnumed.120.252114',
        '3. Blue Earth Diagnostics Inc. POSLUMA® (flotufolastat F 18) Company Core Technical Training Manual. Version 3.0, May 2023.',
      ],
    },
    '8': {
      windowTitle: 'STEP 4: Image Acquisition',
      previousRoute: '/preparation',
      route: '/imagecapture',
      nextRoute: '/imagereconstruction',
      title: 'Recommended Protocol to Ensure Optimal Quality Image Acquisition',
      footnotes: [
        '1. POSLUMA® (flotufolastat F 18) Injection. US Prescribing information; Blue Earth Diagnostics, Ltd. 2023.',
        '2. Blue Earth Diagnostics Inc. POSLUMA® (flotufolastat F 18) Company Core Technical Training Manual. Version 3.0, May 2023.',
        '3. Malaspina S, Oikonen V, Kuisma A, et al. Kinetic analysis and optimisation of 18F-rhPSMA-7.3 PET imaging of prostate cancer. <em>Eur J Nucl Med Mol Imaging.</em> 2021;48(11):3723-3731.',
        '4. Data on File; PP-rh-UK-0039. Blue Earth Diagnostics, Ltd. March 2023',
        '5. Data on File; PP-rh-UK-0040. Blue Earth Diagnostics, Ltd. March 2023',
      ],
    },
    '9': {
      windowTitle: 'STEP 5: Image Reconstruction',
      previousRoute: '/imagecapture',
      route: '/imagereconstruction',
      nextRoute: '',
      title: 'Recommended Protocol to Ensure Optimal Quality Image Acquisition',
      footnotes: [
        'Blue Earth Diagnostics Inc. POSLUMA® (flotufolastat F 18) Company Core Technical Training Manual. Version 3.0, May 2023.',
      ],
    },
  },
  poslumaInterpretation: {
    '1': {
      windowTitle: 'Welcome to the POSLUMA Image Interpretation Training',
      previousRoute: '',
      route: '/welcome',
      nextRoute: '/isi',
      title: 'Welcome to the POSLUMA Image Interpretation Training',
    },
    '2': {
      windowTitle: 'POSLUMA Indication and Important Safety Information',
      route: '/isi',
      nextRoute: '/moa',
      previousRoute: '/welcome',
      title: 'POSLUMA Indication and Important Safety Information',
      footnotes: [
        '1. POSLUMA<sup>®</sup> (flotufolastat F 18) Injection. US Prescribing information; Blue Earth Diagnostics, Ltd. 2023.',
      ],
    },
    '3': {
      windowTitle: 'Background on Prostate Cancer and POSLUMA',
      previousRoute: '/isi',
      route: '/moa',
      nextRoute: '/accurateimaging',
      title: 'Background on Prostate Cancer and POSLUMA',
    },
    '4': {
      windowTitle: 'Recommended Protocol to Ensure the Most Complete Imaging Interpretation',
      previousRoute: '/moa',
      route: '/accurateimaging',
      nextRoute: '/instrumentation',
      title: 'Recommended Protocol to Ensure the Most Complete Imaging Interpretation',
    },
    '5': {
      windowTitle: 'STEP 1: Instrumentation for Use With POSLUMA',
      previousRoute: '/accurateimaging',
      route: '/instrumentation',
      nextRoute: '/patientprep',
      title: 'Recommended Protocol to Ensure the Most Complete Imaging Interpretation',
    },
    '6': {
      windowTitle: 'STEP 2: Patient Preparation and Counseling',
      previousRoute: '/instrumentation',
      route: '/patientprep',
      nextRoute: '/preparation',
      title: 'Recommended Protocol to Ensure the Most Complete Imaging Interpretation',
    },
    '7': {
      windowTitle: 'STEP 3: Preparation and Administration of POSLUMA Dose',
      previousRoute: '/patientprep',
      route: '/preparation',
      nextRoute: '/imagecapture',
      title: 'Recommended Protocol to Ensure the Most Complete Imaging Interpretation',
    },
    '8': {
      windowTitle: 'STEP 4: Image Acquisition',
      previousRoute: '/preparation',
      route: '/imagecapture',
      nextRoute: '/imagereconstruction',
      title: 'Recommended Protocol to Ensure the Most Complete Imaging Interpretation',
    },
    '9': {
      windowTitle: 'STEP 5: Image Reconstruction',
      previousRoute: '/imagecapture',
      route: '/imagereconstruction',
      nextRoute: '/accurateinterpretation',
      title: 'Recommended Protocol to Ensure the Most Complete Imaging Interpretation',
    },
    '10': {
      windowTitle: 'Recommended Protocol to Ensure the Most Complete Imaging Interpretation',
      previousRoute: '/imagereconstruction',
      // We should kind of get BE's approval on this. The Axumin model was "accurateimaging" which is a
      // conflict/duplicate of page 4 above
      route: '/accurateinterpretation',
      nextRoute: '/qualityimage',
      title: 'Recommended Protocol to Ensure the Most Complete Imaging Interpretation',
    },
    '11': {
      windowTitle: 'STEP 1: Review General Interpretation and Read Methodology',
      previousRoute: '/accurateinterpretation',
      route: '/qualityimage',
      nextRoute: '/poslumauptake',
      title: 'Recommended Protocol to Ensure the Most Complete Imaging Interpretation',
      footnotes: [
        '1. Blue Earth Diagnostics Inc. POSLUMA® (flotufolastat F 18) Company Core Technical Training Manual. Version 3.0, May 2023.',
        '2. Froemming AT, Verma S, Eberhardt SC, et al; Expert Panel on Urologic Imaging. ACR Appropriateness Criteria® Post-treatment Follow-up Prostate Cancer. <em> J Am Coll Radiol. </em> 2018;15(5S):S132-S149. doi:10.1016/j.jacr.2018.03.019',
        '3. Chau A, Gardiner P, Colletti PM, Jadvar H. Diagnostic performance of 18F-fluciclovine in detection of prostate cancer bone metastases. <em> Clin Nucl Med. </em> 2018;43(7):e226-e231. doi:10.1097/RLU.0000000000002130',
        '4. Connolly JA, Shinohara K, Presti JC, Carroll PR. Local recurrence after radical prostatectomy: characteristics in size, location, and relationship to prostate-specific antigen and surgical margins. <em> Urology. </em> 1996;47(2):225-231. doi:10.1016/S0090-4295(99)80421-X',
        '5. Arrayeh E, Westphalen AC, Kurhanewicz J, et al. Does local recurrence of prostate cancer after radiation therapy occur at the site of primary tumor? Results of a longitudinal MRI and MRSI study. <em> Int J Radiat Oncol Biol Phys. </em> 2012;82(5):e787-e793. doi:10.1016/j.ijrobp.2011.11.030',
        '6. POSLUMA<sup>®</sup> (flotufolastat F 18) Injection. US Prescribing information; Blue Earth Diagnostics, Ltd. 2023.',
      ],
    },
    '12': {
      windowTitle: 'STEP 2: Evaluate POSLUMA Uptake – Interpretation Methodology',
      previousRoute: '/qualityimage',
      route: '/poslumauptake',
      nextRoute: '/poslumauptake/casestudy',
      title: 'Recommended Protocol to Ensure the Most Complete Imaging Interpretation',
      footnotes: [
        '1. Blue Earth Diagnostics Inc. POSLUMA® (flotufolastat F 18) Company Core Technical Training Manual. Version 3.0, May 2023.',
        '2. Blue Earth Diagnostics Inc. POSLUMA® (flotufolastat F 18) Company Core Technical Training Manual Case Studies. Version 3.0, May 2023.',
        '3. Kiss B, Thoeny HC, Studer UE. Current status of lymph node imaging in bladder and prostate cancer. <em>Urology.</em> 2016;96:1-7. doi:10.1016/j.urology.2016.02.014',
      ],
    },
    '13': {
      windowTitle: 'STEP 2: Evaluate POSLUMA Uptake – Interpretation Methodology (continued)',
      previousRoute: '/poslumauptake',
      route: '/poslumauptake/casestudy',
      nextRoute: '/exclusion',
      title: 'Recommended Protocol to Ensure the Most Complete Imaging Interpretation',
      footnotes: [
        '1. Blue Earth Diagnostics Inc. POSLUMA® (flotufolastat F 18) Company Core Technical Training Manual. Version 3.0, May 2023.',
        '2. Notohamiprodjo S, Eiber M, Lohrmann C, Weber WA. Temporary reactive response of axillary lymph nodes to COVID-19 vaccination on <sup>18</sup>F-rhPSMA-7.3 PET/CT in patients with prostate cancer. <em> J Nucl Med. </em> 2022;63(11):1673-1676. doi:10.2967/jnumed.121.263758',
        '3. Blue Earth Diagnostics Inc. POSLUMA® (flotufolastat F 18) Company Core Technical Training Manual Case Studies. Version 3.0, May 2023.',
        '4. Raj P P, Raj P P, Raj P P. Stellate Ganglion Block. Pain Management. 2007:1191-1198.',
      ],
    },
    '14': {
      windowTitle: 'STEP 3: Exclude Incidental Uptake and Variants',
      previousRoute: '/poslumauptake/casestudy',
      route: '/exclusion',
      nextRoute: '',
      title: 'Recommended Protocol to Ensure the Most Complete Imaging Interpretation',
      footnotes: [
        '1. Blue Earth Diagnostics Inc. POSLUMA® (flotufolastat F 18) Company Core Technical Training Manual. Version 3.0, May 2023.',
      ],
    },
  },
};
